/**
 * Notifications slice — manages the in-app toast / snackbar notification queue.
 *
 * Each notification in the queue is a discrete message with an auto-generated
 * id (via RTK's bundled `nanoid`), a severity level, and a human-readable
 * message string.  The UI layer pops items off this queue to render them.
 */
import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

import { setUser } from '@/features/auth/authSlice';

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  /** Unique id — generated with `nanoid` at enqueue time. */
  id: string;
  /** Severity maps directly to MUI `Alert` color variants. */
  severity: NotificationSeverity;
  /** Human-readable message to display. */
  message: string;
  /** Optional auto-dismiss duration in ms. `undefined` means manual dismiss only. */
  autoHideDuration?: number;
}

/** Payload for adding a new notification (id is auto-generated). */
export type EnqueueNotificationPayload = Omit<Notification, 'id'>;

export interface NotificationsState {
  /** FIFO queue of pending notifications to be displayed. */
  queue: Notification[];
}

const initialState: NotificationsState = {
  queue: [],
};

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Adds a notification to the end of the queue.
     * The `id` is generated automatically using `nanoid`.
     */
    enqueueNotification: {
      reducer(state, action: PayloadAction<Notification>) {
        state.queue.push(action.payload);
      },
      prepare(payload: EnqueueNotificationPayload) {
        return {
          payload: {
            id: nanoid(),
            ...payload,
          },
        };
      },
    },
    /**
     * Removes a notification from the queue by id.
     * Called after the UI has finished displaying / dismissing it.
     */
    dequeueNotification(state, action: PayloadAction<string>) {
      state.queue = state.queue.filter((n) => n.id !== action.payload);
    },
    /** Clears the entire queue (e.g. on logout). */
    clearNotifications(state) {
      state.queue = [];
    },
  },
  extraReducers: (builder) => {
    // When a user successfully (re-)authenticates, drop any pending
    // notifications — especially the "Session expirée. Reconnecte-toi."
    // toast that the 401-refresh path enqueues. Without this, the snackbar
    // sticks around after the user logs back in until the 5 s auto-hide
    // fires (and if a stale polling query 401's during that window, a new
    // toast is enqueued and the message effectively never goes away).
    builder.addCase(setUser, (state) => {
      state.queue = [];
    });
  },
});

export const { enqueueNotification, dequeueNotification, clearNotifications } =
  notificationsSlice.actions;

export default notificationsSlice.reducer;
